import { useRef, useEffect, useState } from "react";
import {
	Box,
	CircularProgress,
	Typography,
	Button,
	TextField,
} from "@mui/material";
import { Link } from "react-router-dom";
import { verifyEmail, resendVerificationEmail } from "../services/api";
import { useTranslation } from "react-i18next";

export default function VerifyEmail() {
	const [status, setStatus] = useState("loading");
	const [email, setEmail] = useState("");
	const [resendLoading, setResendLoading] = useState(false);
	const [resendMessage, setResendMessage] = useState("");

	const { t } = useTranslation();

	const didRequestRef = useRef(false);

	useEffect(() => {
		if (didRequestRef.current) return;
		didRequestRef.current = true;
		const token = new URLSearchParams(window.location.search).get("token");

		if (!token) {
			setStatus("error");
			return;
		}

		const checkEmail = async () => {
			try {
				const data = await verifyEmail(token);
				switch (data.message) {
					case "Email verified successfully":
						setStatus("success");
						break;
					case "Email already verified":
						setStatus("alreadyVerified");
						break;
					case "Token has expired":
						setStatus("expired");
						break;
					default:
						setStatus("error");
						break;
				}
			} catch (err) {
				console.error(err);
				setStatus("error");
			}
		};

		checkEmail();
	}, []);

	const handleResend = async () => {
		if (!email) return;
		setResendLoading(true);
		setResendMessage("");
		try {
			const data = await resendVerificationEmail(email);
			setResendMessage(data.message);
		} catch (err) {
			setResendMessage(
				err?.response?.data?.message || "Nie udało się wysłać maila",
			);
		} finally {
			setResendLoading(false);
		}
	};

	return (
		<Box
			sx={{
				minHeight: "100vh",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				flexDirection: "column",
				gap: 2,
				textAlign: "center",
			}}
		>
			{status === "loading" && <CircularProgress />}

			{status === "success" && (
				<>
					<Typography variant="h5">{t("emailVerified")}</Typography>
					<Typography>{t("emailVerifiedText")}</Typography>
					<Button component={Link} to="/login" variant="contained">
						{t("loginButton")}
					</Button>
				</>
			)}

			{status === "alreadyVerified" && (
				<>
					<Typography variant="h5">
						{t("emailAlreadyVerified")}
					</Typography>
					<Button component={Link} to="/login" variant="contained">
						{t("loginButton")}
					</Button>
				</>
			)}

			{status === "expired" && (
				<>
					<Typography variant="h5" color="error">
						{t("linkExpired")}
					</Typography>
					<Typography>{t("linkExpiredText")}</Typography>
					<TextField
						label="Email"
						value={email}
						onChange={(e) => setEmail(e.target.value)}
					/>
					<Button
						onClick={handleResend}
						variant="contained"
						disabled={resendLoading || !email}
					>
						{resendLoading ? t("resendSending") : t("resendButton")}
					</Button>
					{resendMessage && <Typography>{resendMessage}</Typography>}
				</>
			)}

			{status === "error" && (
				<>
					<Typography variant="h5" color="error">
						{t("invalidOrExpiredLink")}
					</Typography>
					<Button component={Link} to="/login" variant="outlined">
						{t("loginButton")}
					</Button>
				</>
			)}
		</Box>
	);
}
